import React, { useState } from 'react';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Button } from '@components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@components/ui/dialog';
import { CommunityDto } from '@api/ChallengeClient';
import { injectTSDI } from '../core/tsdi';
import { Api } from '../core/api';

interface DeleteCommunityDialogProps {
    open: boolean;
    onClose: () => void;
    community: CommunityDto | null;
    onSuccess: () => void;
}

export const DeleteCommunityDialog: React.FC<DeleteCommunityDialogProps> = ({
    open,
    onClose,
    community,
    onSuccess
}) => {
    const { challengeClient } = injectTSDI(Api);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleDelete = async () => {
        if (!community?.id) return;

        setLoading(true);
        setError(null);

        try {
            await challengeClient.support.communitySupportControllerDeleteCommunity(
                community.id
            );
            onSuccess();
            onClose();
        } catch (err) {
            console.error('Failed to delete community:', err);
            setError('Failed to delete community. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            setError(null);
            onClose();
        }
    };

    return (
        <Dialog open={open} onOpenChange={(next) => !next && handleClose()}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Delete Community</DialogTitle>
                    <DialogDescription>
                        Are you sure you want to delete the community "
                        {community?.name}"?
                    </DialogDescription>
                </DialogHeader>
                <p className="text-sm text-muted-foreground">
                    This action cannot be undone. All community data will be
                    permanently removed.
                </p>
                {error && (
                    <Alert variant="destructive">
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}
                <DialogFooter>
                    <Button
                        variant="ghost"
                        onClick={handleClose}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={handleDelete}
                        loading={loading}
                    >
                        Delete
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
